// krida-ui-special-cards.js - Special cards UI module
// Follows the init(deps) → update(state) → render pattern

let elements = {};

// Client-side card descriptions for player-facing tooltips
export const CARD_DESCRIPTIONS = {
  'the-scout': 'Peek at the top 3 cards of the deck.',
  'the-gardener': 'Gain +1 bonus point immediately.',
  'the-shepherd': 'Combine two board piles into one.',
  'the-merchant': "Peek at your opponent's hand for 5 seconds.",
  'the-swap': "Swap a card from your hand with a random card from your opponent's hand.",
  'the-reaper': 'Harvest any single board pile without playing a card.',
  'the-oracle': 'Reveal all hidden cards in every board pile.',
  'the-tempest': 'Shuffle the top cards of all board piles randomly.',
  'the-thief': 'Steal up to 5 cards from your opponent\'s harvest.',
  'the-flood': 'Sweep the board clean — all pile cards return to the deck, which is reshuffled.',
  'the-plague': 'Remove 3 cards from the top of your opponent\'s harvest from the game.',
  'the-crown': 'Harvest every pile on the board at once.'
};

export function init(dependencies) {
  const container = dependencies?.elements?.specialCardsContent;
  if (!container) {
    console.error("KridaUISpecialCards: specialCardsContent element not provided during initialization.");
    return;
  }
  elements.specialCardsContent = container;
  console.log("KridaUISpecialCards: Initialized.");
}

/**
 * Render special cards UI.
 * @param {string[]} available - Array of cardIds the player can still use
 * @param {string[]} used - Array of cardIds already used this game
 * @param {boolean} isMyTurn - Whether it's the current player's turn
 * @param {boolean} specialCardsEnabled - Whether special cards are enabled for this game
 */
export function updateSpecialCards(available, used, isMyTurn, specialCardsEnabled) {
  if (!elements.specialCardsContent) return;

  const container = elements.specialCardsContent;

  // Hide entire UI when special cards are disabled or no cards exist
  const parentSection = container.closest('.special-cards-area');
  if (!specialCardsEnabled || (!available?.length && !used?.length)) {
    if (parentSection) parentSection.classList.add('hidden');
    container.innerHTML = '';
    return;
  }

  if (parentSection) parentSection.classList.remove('hidden');

  container.innerHTML = '';
  container.classList.toggle('not-my-turn', !isMyTurn);

  // Render available cards
  if (available && available.length > 0) {
    available.forEach(cardId => {
      const cardEl = _createSpecialCardElement(cardId, false, isMyTurn);
      container.appendChild(cardEl);
    });
  }

  // Render used cards
  if (used && used.length > 0) {
    used.forEach(cardId => {
      const cardEl = _createSpecialCardElement(cardId, true, false);
      container.appendChild(cardEl);
    });
  }

  console.log(`KridaUISpecialCards: Updated - ${available?.length || 0} available, ${used?.length || 0} used`);
}

/**
 * Trigger play animation on a special card before it transitions to used state.
 * @param {string} cardId - The card that was played
 */
export function playAnimation(cardId) {
  if (!elements.specialCardsContent) return;
  const cardEl = elements.specialCardsContent.querySelector(`.special-card[data-card-id="${cardId}"]`);
  if (!cardEl) return;

  cardEl.classList.add('activating');
  cardEl.addEventListener('animationend', () => {
    cardEl.classList.remove('activating');
  }, { once: true });
}

function _createSpecialCardElement(cardId, isUsed, isMyTurn) {
  const el = document.createElement('div');
  el.className = 'special-card';
  el.dataset.cardId = cardId;

  // Format display name from cardId (e.g., "the-thief" → "The Thief")
  const displayName = cardId
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

  const description = CARD_DESCRIPTIONS[cardId] || '';

  el.innerHTML = `
    <span class="special-card-icon">★</span>
    <span class="special-card-name">${displayName}</span>
    ${description ? `<span class="special-card-desc">${description}</span>` : ''}
  `;

  if (description) {
    el.setAttribute('title', description);
  }

  if (isUsed) {
    el.classList.add('used');
    el.setAttribute('aria-disabled', 'true');
  } else if (!isMyTurn) {
    el.classList.add('disabled');
    el.setAttribute('aria-disabled', 'true');
  } else {
    el.addEventListener('click', () => {
      const event = new CustomEvent('krida-special-card-selected', {
        bubbles: true,
        cancelable: true,
        detail: { cardId }
      });
      el.dispatchEvent(event);
    });
  }

  return el;
}
